// useScannerEnterGuard — the reader's OWN Enter never acts on what its scan
// opened.
//
// A keyboard-wedge reader types the code and, on most configurations, then
// sends Enter (the DT50S can be set either way). useScanWedge recognises a
// fixed-shape code on its LAST CHARACTER, no terminator needed, and React
// flushes that keydown's update before the next key event arrives. So whatever
// the scan opens is already on screen and focused when the reader's Enter
// lands — and the Enter acts on it:
//
//   - Capture's count box: its input is autofocused and Enter = Save, so the
//     preset "1" was saved before anyone could type (H1 D1 — a 3-bin drop
//     booked as 1).
//   - a scan's warning: its OK button takes focus, so the warning closed in
//     the same instant it opened, unread (H1 D12).
//
// `arm()` opens a short window in which every Enter keydown is cancelled at the
// DOCUMENT, in the CAPTURE phase — before any element, the React root, a
// dialog's button or its default action sees it. Every Enter, not just the
// first: a reader set to send CR+LF delivers two. Nobody reads a name and
// presses Enter inside 300 ms, so a person's Enter after it saves or confirms
// exactly as before.
//
// The window is measured TWO ways and either one cancels: when the Enter is
// DISPATCHED (Date.now()), and when it was CREATED (`e.timeStamp`, on the
// performance.now() clock). The scan's own render — the count box and the page
// behind it — runs before the queued reader Enter is dispatched, and on a slow
// floor phone (Chrome 86 handsets) that render alone can outlast 300 ms; the
// creation time is still inside the window then. A timeStamp on the epoch clock
// (jsdom; very old engines) is not comparable, so only the dispatch rule runs.
//
// Deliberately NOT inside useScanWedge: the screen that opened something knows
// it did, and the hook does not (Capture disarms it while the count box is
// open, so a guard there could not see the Enter at all). This works whether
// or not the scan hook swallows a trailing Enter of its own.

import { useCallback, useEffect, useRef } from 'react'

/** How long after a scan an Enter is taken to be the reader's, not a person's. */
export const SCANNER_ENTER_WINDOW_MS = 300

/** Returns `arm()` — call it the moment a scan is recognised. */
export function useScannerEnterGuard(windowMs = SCANNER_ENTER_WINDOW_MS): () => void {
  // When arm() ran, on both clocks; never armed = -Infinity, so nothing is inside.
  const armed = useRef({ wall: -Infinity, perf: -Infinity })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !insideWindow(armed.current, e.timeStamp, windowMs)) return
      e.preventDefault()
      e.stopImmediatePropagation()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [windowMs])

  return useCallback(() => { armed.current = { wall: Date.now(), perf: performance.now() } }, [])
}

/** Both clocks at the moment a scan was recognised. */
export type ArmedAt = { wall: number; perf: number }

/** A timeStamp this large is epoch ms, not ms since the page's time origin. */
const EPOCH_STAMP = 1e12

export function insideWindow(armed: ArmedAt, created: number, windowMs: number): boolean {
  if (Date.now() <= armed.wall + windowMs) return true
  // Created before arm() ran (the reader typed faster than the page) counts too.
  return created < EPOCH_STAMP && created - armed.perf <= windowMs
}
